import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { cn } from "../lib/utils";
import { type DemandeReglage, type Reglage, useRegler } from "./bobines-requetes";
import { COMPTEURS, refusEnClair } from "./bobines-textes";
import { MINIMUM_MAX, RESERVE_MAX, type ReferenceVue } from "./contract";

/**
 * Les deux compteurs d'une ligne du stock (spec, section 4, et son amendement du 28/09/2026 ;
 * maquette `BobinesEtats.dc.html`, etats 7 et 8) : le minimum de bobines neuves, de 0 a 20, et
 * la reserve, les bobines neuves de l'etagere, de 0 a 99. « - » et « + », sans bouton
 * « Enregistrer ». Chaque clic envoie son `PUT` ; pendant l'envoi, les deux boutons du compteur
 * s'eteignent et la ligne dit « enregistrement... », puis la vue se relit. Un refus s'ecrit
 * sous la ligne, et la valeur revient a la derniere connue.
 *
 * Un seul envoi a la fois par compteur : les boutons eteints ignorent le clic, et un verrou
 * synchrone attrape le second clic d'un double clic, arrive avant que React ait redessine.
 * Eteints, les boutons restent focalisables (`aria-disabled`) : un `disabled` ferait tomber le
 * focus du clavier sur la page au premier clic. L'envoi ne s'acheve qu'une fois la vue relue :
 * le clic suivant part toujours de la valeur de la base.
 *
 * La reserve s'envoie avec `avant`, la valeur affichee : si une copie l'a fait baisser entre la
 * lecture de la page et le clic, la console refuse (409), la vue se relit, et la ligne dit de
 * refaire le geste. Deux ecritures parties de la meme valeur (la ligne et le choix du catalogue)
 * ne s'ecrasent donc jamais : la seconde est refusee.
 *
 * Le bouton clique reste a sa place a l'ecran pendant l'envoi (`Ancre`). Le choix d'une couleur
 * du catalogue (`bobines-reserve.tsx`) passe par le meme envoi tenu (`useEnvoiTenu`).
 */

/** La borne haute de chaque reglage, celle du contrat. */
export const MAXIMUMS: Readonly<Record<Reglage, number>> = {
  minimum: MINIMUM_MAX,
  reserve: RESERVE_MAX,
};

/**
 * Le bouton clique, tenu a sa hauteur dans l'ecran jusqu'a la fin de l'envoi. Sur le telephone,
 * la liste de courses est au-dessus du stock : la relecture qui y ajoute ou retire une ligne
 * poussait tout le stock de 56 px, et le tap suivant tombait sur la ligne voisine. L'ancrage du
 * defilement du navigateur ne compense pas tant que la liste de courses est a l'ecran : la page
 * rattrape donc l'ecart elle-meme, apres chaque rendu (`garderEnPlace`).
 *
 * Un geste de Nicolas (molette, doigt qui glisse) lache le bouton : c'est lui qui fait defiler
 * la page, elle ne la lui reprend pas.
 */
type Ancre = {
  readonly element: HTMLElement;
  /** Le haut du bouton dans l'ecran, au clic. */
  readonly haut: number;
  /** Lachee par un geste : plus rien a rattraper. */
  lachee: boolean;
  /**
   * Pose par `onSettled`, juste avant le rendu de la fin de l'envoi : ce rendu rattrape encore
   * l'ecart, puis lache l'ancre. Pas `isPending` : un envoi rapide peut passer d'un rendu a
   * l'autre sans jamais etre rendu « en cours ».
   */
  envoiFini: boolean;
  lacher(): void;
};

const GESTES = ["wheel", "touchmove"] as const;

function ancrer(element: HTMLElement): Ancre {
  const ancre: Ancre = {
    element,
    haut: element.getBoundingClientRect().top,
    lachee: false,
    envoiFini: false,
    lacher() {
      ancre.lachee = true;
      for (const geste of GESTES) window.removeEventListener(geste, ancre.lacher);
    },
  };
  for (const geste of GESTES) window.addEventListener(geste, ancre.lacher, { passive: true });
  return ancre;
}

/** Rattrape le decalage du bouton ancre ; le lache au rendu de la fin de l'envoi. */
function garderEnPlace(ancre: RefObject<Ancre | null>): void {
  const tenue = ancre.current;
  if (tenue === null) return;
  if (!tenue.lachee && tenue.element.isConnected) {
    const ecart = tenue.element.getBoundingClientRect().top - tenue.haut;
    // D'un bond : la console ne pose pas de `scroll-behavior` (un `scroll-smooth` sur la racine
    // ferait glisser le rattrapage). Pas d'option `behavior: "instant"`, qu'un Safari ancien
    // refuserait d'une TypeError, en plein rendu.
    if (Math.abs(ecart) >= 1) window.scrollBy(0, ecart);
  }
  if (tenue.envoiFini) {
    tenue.lacher();
    ancre.current = null;
  }
}

/** Un envoi de reglage a la fois, son bouton tenu sous le doigt jusqu'a la fin de la relecture. */
export type EnvoiTenu = {
  readonly enCours: boolean;
  /** La demande qui part, pendant l'envoi seulement. */
  readonly enVol: DemandeReglage | undefined;
  /** Le refus du dernier envoi, ou null. */
  readonly erreur: Error | null;
  /**
   * Envoie `demande`, sauf si un envoi est deja en vol (rend alors `false`). `bouton`, le
   * bouton clique, reste a sa place a l'ecran ; `fini` dit, a la fin de la relecture, si le
   * reglage est passe.
   */
  envoyer(demande: DemandeReglage, bouton?: HTMLElement, fini?: (passe: boolean) => void): boolean;
};

export function useEnvoiTenu(quoi: Reglage): EnvoiTenu {
  const envoi = useRegler(quoi);
  const verrou = useRef(false);
  const ancre = useRef<Ancre | null>(null);
  // Apres chaque rendu : c'est la relecture, rendue avec le composant, qui decale la page.
  useLayoutEffect(() => garderEnPlace(ancre));
  // Le composant remplace (session perdue, choix ferme) : les ecouteurs du geste partent avec lui.
  useEffect(() => () => ancre.current?.lacher(), []);
  return {
    enCours: envoi.isPending,
    enVol: envoi.isPending ? envoi.variables : undefined,
    erreur: envoi.isError ? envoi.error : null,
    envoyer(demande, bouton, fini) {
      if (verrou.current) return false;
      verrou.current = true;
      ancre.current?.lacher();
      const tenue = bouton === undefined ? null : ancrer(bouton);
      ancre.current = tenue;
      envoi.mutate(demande, {
        onSettled: (_reponse, erreur) => {
          verrou.current = false;
          if (tenue !== null) tenue.envoiFini = true;
          fini?.(erreur === null);
        },
      });
      return true;
    },
  };
}

export type EtatCompteur = {
  /** La valeur affichee : celle qui part pendant l'envoi, la valeur lue sinon. */
  readonly valeur: number;
  readonly enCours: boolean;
  /** Le refus du dernier envoi, en clair, ou null. */
  readonly refus: string | null;
  /** Envoie `valeur` ; `bouton`, le bouton clique, reste a sa place a l'ecran pendant l'envoi. */
  regler(valeur: number, bouton?: HTMLElement): void;
};

export function useCompteur(
  quoi: Reglage,
  reference: Pick<ReferenceVue, "cle" | "minimum" | "reserve">,
): EtatCompteur {
  const envoi = useEnvoiTenu(quoi);
  const lue = reference[quoi];
  return {
    valeur: envoi.enVol === undefined ? lue : envoi.enVol.valeur,
    enCours: envoi.enCours,
    refus: envoi.erreur === null ? null : refusEnClair(quoi, envoi.erreur, lue),
    regler(valeur, bouton) {
      if (valeur < 0 || valeur > MAXIMUMS[quoi]) return;
      // `avant` : ce que la ligne affiche, pour que la reserve ne s'ecrive que si la base le
      // porte encore (le minimum l'ignore).
      envoi.envoyer({ cle: reference.cle, valeur, avant: lue }, bouton);
    },
  };
}

/**
 * L'anneau de focus des deux boutons, a l'interieur du bouton : colles l'un a la valeur, un
 * anneau dehors passerait sous la case voisine.
 */
const FOCUS_DEDANS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset";

const BOUTON =
  "inline-flex size-[34px] items-center justify-center text-base text-foreground hover:bg-muted md:size-7 md:text-[15px]";
const BOUTON_ETEINT =
  "inline-flex size-[34px] cursor-default items-center justify-center text-base text-muted-foreground/50 md:size-7 md:text-[15px]";

/**
 * Les trois cases de la maquette : « - », la valeur, « + ». `nom` est le nom complet de la
 * reference (« Black, PLA Basic », `nomComplet`) : la couleur seule se repete d'une gamme a
 * l'autre, et deux boutons de la page ne portent jamais le meme nom.
 */
export function Compteur({ quoi, nom, etat }: { quoi: Reglage; nom: string; etat: EtatCompteur }) {
  const textes = COMPTEURS[quoi];
  const baisserEteint = etat.enCours || etat.valeur <= 0;
  const monterEteint = etat.enCours || etat.valeur >= MAXIMUMS[quoi];
  return (
    <fieldset
      aria-label={textes.groupe(nom)}
      className="inline-flex min-w-0 items-center rounded-lg border border-input bg-card"
    >
      <button
        aria-disabled={baisserEteint || undefined}
        aria-label={textes.baisser(nom)}
        className={cn(baisserEteint ? BOUTON_ETEINT : BOUTON, FOCUS_DEDANS, "rounded-l-lg")}
        onClick={(evenement) => {
          if (!baisserEteint) etat.regler(etat.valeur - 1, evenement.currentTarget);
        }}
        type="button"
      >
        -
      </button>
      {/* Grise pendant l'envoi, et a 0 : un minimum a 0 ne rachete jamais rien, une reserve a 0
          n'a rien sur l'etagere. */}
      <span
        aria-live="polite"
        className={cn(
          "inline-flex h-[34px] w-[30px] items-center justify-center border-x border-input text-sm tabular-nums md:h-7 md:w-8",
          etat.enCours || etat.valeur === 0
            ? "text-muted-foreground"
            : "font-semibold text-foreground",
        )}
        data-compteur={quoi}
      >
        {etat.valeur}
      </span>
      <button
        aria-disabled={monterEteint || undefined}
        aria-label={textes.monter(nom)}
        className={cn(monterEteint ? BOUTON_ETEINT : BOUTON, FOCUS_DEDANS, "rounded-r-lg")}
        onClick={(evenement) => {
          if (!monterEteint) etat.regler(etat.valeur + 1, evenement.currentTarget);
        }}
        type="button"
      >
        +
      </button>
    </fieldset>
  );
}

/** La roue et « enregistrement... », pendant l'envoi d'un reglage. */
export function Enregistrement() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
      <span
        aria-hidden="true"
        className="inline-block size-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground motion-reduce:animate-none"
      />
      enregistrement...
    </span>
  );
}
